import { useCallback, useEffect, useState } from 'react';

import { Trans } from '@lingui/react/macro';
import type { GridColDef } from '@mui/x-data-grid';
import { useHistory } from 'react-router';

import type {
  InsightsPracticeAiMetrics,
  InsightsPracticeTreatments,
} from '../../../../@types/contracts';
import { useGetInsightsPractices } from '../../../../react-query/useGetInsightsPractices';
import routes from '../../../../routes/routes.constants';
import {
  getCurrentDate,
  getFirstDateOfMonth,
} from '../../../../utils/date.util';
import { Loading } from '../../common/loading/Loading';
import { parseDollars, parsePercentage } from '../../common/utils';
import {
  ASC,
  DEFAULT_PAGE_SIZE,
  DESC,
  NAME,
  PAGE,
  REVENUE,
} from '../../constants';

import { StyledDataGrid } from './styles';

const mapPracticeToRowContents = (item: RowProps) => {
  const totalPresented = item?.treatments?.totalPresented || 0;
  const totalCompleted = item?.treatments?.totalCompleted || 0;
  const caseCompletionQuantity = item?.treatments?.caseCompletionQuantity || 0;
  const caseCompletionRevenue = item?.treatments?.caseCompletionRevenue || 0;
  const id = item?.id || encodeURIComponent(item?.practiceName);
  const revenue = parseDollars(item?.revenue?.totalRestorative);
  const uniquePatientsRestoAiFindings =
    item?.aiMetrics?.uniquePatientsRestoAiFindings || 0;

  return {
    id: id,
    name: item?.practiceName,
    revenue: revenue,
    aiFindings: item?.aiMetrics?.totalFindings?.toLocaleString(),
    uniquePatientsRestoAiFindings: uniquePatientsRestoAiFindings,
    treatmentsPresented: totalPresented.toLocaleString(),
    treatmentsCompleted: totalCompleted.toLocaleString(),
    caseCompletionCounts: `${parsePercentage(caseCompletionQuantity)} %`,
    caseCompletionDollars: `${parsePercentage(caseCompletionRevenue)} %`,
    patientDiagnosticPercentage: `${parsePercentage(
      item?.aiMetrics?.aiDiagnosticRate,
    )} %`,
  };
};

interface Props {
  user: {
    primaryOrganizationId: string;
  };
  sortBy: string;
  order: string;
  hideColumns: unknown;
  hideFooter: boolean;
  context: string;
  pageSize?: number;
}

interface RowProps {
  id: string;
  practiceName: string;
  revenue: { totalRestorative: number };
  treatments?: InsightsPracticeTreatments;
  aiMetrics?: InsightsPracticeAiMetrics;
}

export const LocationsTable = ({
  user,
  sortBy = NAME,
  order = ASC,
  hideColumns = {},
  hideFooter,
  context = PAGE,
  pageSize = DEFAULT_PAGE_SIZE,
}: Props) => {
  const [sortLocations, setSortLocations] = useState({
    sortBy: sortBy,
    order: order,
  });

  const getRowSpacing = useCallback(() => {
    return {
      top: 0,
      bottom: 8,
    };
  }, []);

  const [paginationModel, setPaginationModel] = useState({
    pageSize: pageSize,
    page: 0,
    itemsCount: 0,
  }) as any;

  // TODO: MUI Datagrid pagination is 0-indexed, but the API is 1-indexed. Maybe decide on one or the other...
  const { data: locations } = useGetInsightsPractices({
    organizationId: user.primaryOrganizationId,
    startDate: getFirstDateOfMonth(),
    endDate: getCurrentDate(),
    sortBy: sortLocations.sortBy,
    order: sortLocations.order,
    pageSize: paginationModel.pageSize,
    page: paginationModel.page + 1,
  });

  const meta = locations?.meta;

  const [rowCountState, setRowCountState] = useState(meta?.itemsCount || 0);

  useEffect(() => {
    setRowCountState((prevRowCountState: any) =>
      meta?.itemsCount !== undefined ? meta?.itemsCount : prevRowCountState,
    );
  }, [meta, locations]);

  const history = useHistory();

  if (!locations) {
    return <Loading />;
  }

  const rows = locations?.data.map((item) => mapPracticeToRowContents(item));

  const shouldDisplaySortControls = context === PAGE;
  const isCurrentSort = (field: string) => sortLocations.sortBy === field;
  const isAscSort = sortLocations.order === ASC;

  const handleSort = (field: string) => {
    const newOrder = !isAscSort ? ASC : DESC;
    setSortLocations({
      sortBy: field,
      order: newOrder,
    });
  };

  const handleRowClick = (params: { id: any }) => {
    history.push(`${routes.VIDEA_INSIGHT_LOCATIONS}/${params.id}`);
  };

  const sortIndicator = (
    <span className="sort-indicator">
      {sortLocations.order === ASC ? '▲' : '▼'}
    </span>
  );

  const LOCATIONS_COLUMNS: GridColDef[] = [
    {
      field: 'id',
      headerName: 'Practice Id',
      description: '',
      minWidth: 50,
      type: 'string',
      editable: false,
      sortable: false,
    },
    {
      field: NAME,
      headerName: 'Practice Name',
      description: '',
      flex: 1,
      minWidth: 200,
      type: 'string',
      editable: false,
      sortable: false,
      renderHeader: () =>
        shouldDisplaySortControls ? (
          <button onClick={() => handleSort(NAME)} className="sort-control">
            <Trans>
              Practice Name
              {isCurrentSort(NAME) && sortIndicator}
            </Trans>
          </button>
        ) : (
          <span className="column-head">
            <Trans>Practice Name</Trans>
          </span>
        ),
    },
    {
      field: REVENUE,
      headerName: 'Production',
      description: '',
      flex: 1,
      minWidth: 160,
      type: 'number',
      editable: false,
      sortable: false,
      renderHeader: () =>
        shouldDisplaySortControls ? (
          <button onClick={() => handleSort(REVENUE)} className="sort-control">
            <Trans>
              Production
              {isCurrentSort(REVENUE) && sortIndicator}
            </Trans>
          </button>
        ) : (
          <span className="column-head">
            <Trans>Production</Trans>
          </span>
        ),
    },
    {
      field: 'aiFindings',
      headerName: 'AI Findings',
      description: 'AI Findings (Past 30 Days)',
      flex: 1,
      minWidth: 80,
      type: 'number',
      editable: false,
      sortable: false,
    },
    {
      field: 'uniquePatientsRestoAiFindings',
      headerName: 'Patients with AI Findings',
      description:
        'Unique Patients with Restorative AI Findings (Past 30 Days)',
      flex: 1,
      minWidth: 80,
      type: 'number',
      editable: false,
      sortable: false,
    },
    {
      field: 'treatmentsPresented',
      headerName: 'Treatments Presented',
      description: 'Count of all Restorative Treatments Presented',
      flex: 1,
      minWidth: 80,
      type: 'number',
      editable: false,
      sortable: false,
    },
    {
      field: 'treatmentsCompleted',
      headerName: 'Treatments Completed',
      description: 'Count of all Restorative Treatments Completed',
      flex: 1,
      minWidth: 80,
      type: 'number',
      editable: false,
      sortable: false,
    },
    {
      field: 'caseCompletionCounts',
      headerName: 'Case Completion (Counts)',
      description: 'Percentage of Tx Completed, by Tx Count',
      flex: 1,
      minWidth: 80,
      type: 'number',
      editable: false,
      sortable: false,
    },
    {
      field: 'caseCompletionDollars',
      headerName: 'Case Completion, (Dollars)',
      description: 'Percentage of Tx Completed, by Tx Dollars',
      flex: 1,
      minWidth: 80,
      type: 'number',
      editable: false,
      sortable: false,
    },
    {
      field: 'patientDiagnosticPercentage',
      headerName: 'Patient Diagnostic Percentage',
      description:
        'Percentage of Restorative Tx with corresponding AI Findings',
      flex: 1,
      minWidth: 95,
      type: 'number',
      editable: false,
    },
  ];

  const colVisible =
    typeof hideColumns === 'object'
      ? { id: false, ...hideColumns }
      : { id: false };

  return (
    <StyledDataGrid
      className="data-grid practice-table"
      rows={rows}
      columns={LOCATIONS_COLUMNS}
      getRowSpacing={getRowSpacing}
      rowCount={rowCountState}
      onRowClick={handleRowClick}
      disableRowSelectionOnClick
      disableColumnMenu
      paginationModel={paginationModel}
      paginationMode="server"
      onPaginationModelChange={setPaginationModel}
      initialState={{
        columns: {
          columnVisibilityModel: colVisible,
        },
      }}
      hideFooter={hideFooter}
      hideFooterPagination={hideFooter}
      hideFooterSelectedRowCount={hideFooter}
    />
  );
};
